import React, { useState, useMemo, useEffect } from 'react';
import { useAssetsStore } from '../../hooks/useAssetsStore';
import { useLocationForSorting } from '../../hooks/useLocationForSorting';
import { useAssetSearch } from '../../hooks/useAssetSearch';
import { SearchInput } from '../../components/SearchInput';
import { CreateAssetButton } from "../../components/createAssetButton/CreateAssetButton";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import { AssetUtils } from '../../services/asset/asset-api-service';
import { highlightSearchTerm } from '../../utils/SearchUtils';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import { fetchCurrentUser } from '../../store/user';

const AssetList: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const navigate = useNavigate();
    const { t } = useTranslation();

    const { loading, getChildAssets, assets } = useAssetsStore();
    const { userCoords, locationError } = useLocationForSorting();

    const dispatch = useDispatch<AppDispatch>();
    const { currentUser } = useSelector((state: RootState) => state.currentUser);

    const buildingAssets = useMemo(() => {
        const buildings = assets.filter(asset => {
            const systemType = (asset.assetType as any)?.assetSystemType;
            return systemType === 1 || systemType === 2;
        });
        
        if (userCoords && buildings.length > 0) {
            return buildings.sort((a, b) => {
                const aDistance = typeof a.distanceFromUser === 'number' && !isNaN(a.distanceFromUser) ? a.distanceFromUser : Infinity;
                const bDistance = typeof b.distanceFromUser === 'number' && !isNaN(b.distanceFromUser) ? b.distanceFromUser : Infinity;
                return aDistance - bDistance;
            });
        }
        
        return buildings;
    }, [assets, userCoords]);

    useEffect(() => {
        if (!currentUser && localStorage.getItem('access_token')) {
          dispatch(fetchCurrentUser());
        }
      }, [currentUser]);

    const filteredAssets = useAssetSearch(buildingAssets, searchTerm, userCoords);

    const handleAssetClick = (asset: any) => {
        navigate(`/assets/${asset.id}`);
    };

    return (
        <>
            {message && (
                <div className={message.type === 'success' ? 'success' : 'error'}>
                    {message.text}
                </div>
            )}

            {locationError && (
                <div className="warning" style={{
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    padding: '8px 12px',
                    margin: '8px 0',
                    borderRadius: '4px',
                    fontSize: '0.9em'
                }}>
                    {locationError}
                </div>
            )}

            <div className="section-home">
                <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder={t('itemSearch', 'Search buildings by name...')}
                    className="search-input"
                    showResultsCount={true}
                    resultsCount={filteredAssets.length}
                    totalCount={buildingAssets.length}
                />

                {(filteredAssets && filteredAssets.length > 0) ? (
                    <div className="asset-list">
                        {filteredAssets.map((asset) => {
                            const displayInfo = AssetUtils.formatAssetForDisplay(asset);
                            const showDistance = userCoords &&
                                typeof asset.distanceFromUser === 'number' &&
                                asset.distanceFromUser !== Infinity &&
                                !isNaN(asset.distanceFromUser);

                            return (
                                <div
                                    key={asset.id || asset.parentId || Math.random()}
                                    className="asset-simple-item"
                                    onClick={() => handleAssetClick(asset)}
                                >
                                    <i className={`icon-style ${asset.assetType?.icon || ''}`}></i>
                                    <div className="asset-content">
                                        <div className="asset-simple-name">
                                            {highlightSearchTerm(displayInfo.title, searchTerm)}
                                            {showDistance && (
                                                <span className="distance-badge">
                                                    {asset.formattedDistance}
                                                </span>
                                            )}
                                        </div>
                                        <div className="asset-simple-type">
                                            {displayInfo.type}
                                        </div>
                                    </div>

                                    {getChildAssets(asset.id).length > 0 && (
                                        <div className="has-children-indicator">
                                            <img
                                                src="/img/hierarchy.svg"
                                                alt="Has child assets"
                                                className="hierarchy-icon"
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : searchTerm ? (
                    <p className="no-assets">{t('noAssetsFoundMatching', 'No building assets found matching')} "{searchTerm}"</p>
                ) : loading ? (
                    <p className="no-assets">{t('noAssetsFound', 'Loading assets...')}</p>
                ) : buildingAssets.length === 0 ? (
                    <p className="no-assets">No building assets found.</p>
                ) : (
                    <p className="no-assets">{t('noAssetsFound', 'No building assets found.')}</p>
                )}
            </div>

            <div className="build-nr">
                <p>{process.env.REACT_APP_BUILD_NUMBER}</p>
            </div>
        </>
    );
};

export default AssetList;